import {
  MinimumEditorMode,
  MinimumEditorRootProps
} from 'modules/Cube/view/common/drawers/minimumEditor/drawer/minimumEditor.types'
import uniq from 'lodash/fp/uniq'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import { Minimum, Phase } from 'modules/Cube/domain/cube.domain.types'
import { minimumEditorCubeAdapter } from 'modules/Cube/view/common/drawers/minimumEditor/minimumEditor.adapters'
import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { applyNewMinimumToPhases } from '@sequencehq/utils'
import { match } from 'ts-pattern'
import { useFlashEntities } from 'Cube/utils/useFlashEntities'

type UseMinimumsEditorConnector = () => MinimumEditorRootProps

export const useMinimumsEditorConnector: UseMinimumsEditorConnector = () => {
  const navigate = useNavigate()
  const routeParams = useParams<{
    phaseId: Phase['id']
    minimumId: Minimum['id']
  }>()
  const cubeContext = useCubeContext()
  const flashEntities = useFlashEntities()
  const phaseEditingMode =
    cubeContext.queries.rawData.configuration.editorDefaults.phaseEditingMode

  useEffect(() => {
    if (
      !routeParams.phaseId ||
      !cubeContext.queries.rawData.data.phases[routeParams.phaseId]
    ) {
      /**
       * Bail if we are trying to access the editor for a phase that doesn't exist
       */
      navigate('..')
    }
  }, [cubeContext, navigate, routeParams.phaseId])

  const existingMinimum = useMemo(() => {
    if (!routeParams.phaseId || !routeParams.minimumId) {
      return null
    }

    const existingData = cubeContext.queries.resolvedPhases[
      routeParams.phaseId
    ]?.minimums.find(minimum => minimum.id === routeParams.minimumId)

    /**
     * If we attempted to access the editor for a minimum that doesn't exist, also bail.
     */
    if (!existingData) {
      navigate('..')
      return null
    }

    return minimumEditorCubeAdapter.in(existingData)
  }, [cubeContext, routeParams, navigate])

  const handleClose = useCallback(() => {
    navigate('..')
  }, [navigate])

  const currentPhase = routeParams.phaseId
    ? cubeContext.queries.resolvedPhases[routeParams.phaseId]
    : null

  const handleSave = useCallback(
    (newMinimum: Minimum) => {
      if (!routeParams.phaseId || !currentPhase) {
        return
      }

      const adaptedMinimum = minimumEditorCubeAdapter.out(newMinimum)
      /**
       * How far a pricing edit reaches is an account setting. On 'SINGLE' only
       * the phase being edited is passed, so the update stops there; on
       * 'CASCADE_TO_FUTURE_PHASES' every phase is passed and the update carries
       * on into each later phase that still has the same minimum, stopping at
       * the first one that does not.
       */
      const updates = applyNewMinimumToPhases({
        newMinimum: adaptedMinimum,
        phaseId: currentPhase.id,
        phases: match(phaseEditingMode)
          .with(
            'CASCADE_TO_FUTURE_PHASES',
            () => cubeContext.queries.orderedPhases
          )
          .with('SINGLE', () => [currentPhase])
          .exhaustive()
      })

      cubeContext.mutators.updateData({
        minimums: updates.updatedMinimums,
        phases: updates.updatedPhases
      })

      flashEntities(Object.keys(updates.updatedMinimums))

      navigate('..')
    },
    [
      navigate,
      currentPhase,
      cubeContext.mutators,
      cubeContext.queries.orderedPhases,
      routeParams.phaseId,
      flashEntities,
      phaseEditingMode
    ]
  )

  const currency = useMemo(() => {
    return (
      cubeContext.queries.selectedCurrency ??
      cubeContext.queries.rawData.configuration.currency.default
    )
  }, [
    cubeContext.queries.selectedCurrency,
    cubeContext.queries.rawData.configuration.currency.default
  ])

  const mode = useMemo(() => {
    if (!routeParams.phaseId) {
      return MinimumEditorMode.VIEW
    }

    if (!routeParams.minimumId) {
      const addMinimumAvailable =
        cubeContext.queries.availableFeatures.phases[routeParams.phaseId].phase
          .minimum.add.available.enabled

      if (!addMinimumAvailable) {
        navigate('..')
        return MinimumEditorMode.VIEW
      }

      return MinimumEditorMode.CREATE
    }

    const editMinimumAvailable =
      cubeContext.queries.availableFeatures.phases[routeParams.phaseId].phase
        .minimum.edit.available.enabled

    return editMinimumAvailable
      ? MinimumEditorMode.EDIT
      : MinimumEditorMode.VIEW
  }, [cubeContext, routeParams.phaseId, routeParams.minimumId, navigate])

  /**
   * For minimums, we only want to include products which have a usage based
   * price
   */
  const availablePrices = useMemo(() => {
    if (
      !routeParams.phaseId ||
      !cubeContext.queries.resolvedPhases[routeParams.phaseId]?.prices
    ) {
      return []
    }

    return cubeContext.queries.resolvedPhases[
      routeParams.phaseId
    ]?.prices.filter(price => 'usageMetricId' in price.structure)
  }, [cubeContext, routeParams.phaseId])

  const applyToAllAvailable = useMemo(() => {
    const allRecurringFrequencies = uniq(
      availablePrices
        .map(price =>
          price.billingFrequency !== 'ONE_TIME'
            ? price.billingFrequency
            : undefined
        )
        .filter(Boolean)
    )

    return allRecurringFrequencies.length < 2
  }, [availablePrices])

  return {
    existingMinimum,
    availablePrices,
    currency,
    applyToAllAvailable,
    mode,
    onClose: handleClose,
    onSave: handleSave
  }
}
